<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMagicKeys } from '@vueuse/core'
import { loadStripe } from '@stripe/stripe-js'
import * as Sentry from '@sentry/vue'
import { RouterLinkBack, VButton, VButtonInvisible, VSection } from '@/modules/shared/components'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { useCustomerStore } from '../stores/customer-store'

const router = useRouter()
const route = useRoute()
const customerStore = useCustomerStore()

///////////////////////////////////////////////////////////////////////////////
// Keyboard shortcuts
///////////////////////////////////////////////////////////////////////////////

const { escape } = useMagicKeys()

const back_route = computed(() => {
  if (route.params.billable_type) {
    return { name: 'billing-entity' }
  }
  return { name: 'billing-group' }
})

watch(escape, (v) => {
  if (v) router.push(back_route.value)
})

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const loading = ref(false)
const error_message = ref('')
const descriptor_code = ref('')
const amount_1 = ref('')
const amount_2 = ref('')

const setup_intent = computed(() => {
  const id = route.params.setup_intent_id
  return customerStore.pending_setup_intents.find((si: any) => si.stripe_setup_intent_id === id)
})

const microdeposit_type = computed(() => {
  return setup_intent.value?.next_action?.verify_with_microdeposits?.microdeposit_type
})

const payment_method = computed(() => {
  if (!setup_intent.value?.stripe_payment_method_id) return null
  return customerStore.customer?.stripe_payment_methods?.find(
    (pm: any) => pm.stripe_payment_method_id === setup_intent.value.stripe_payment_method_id,
  )
})

const bank_label = computed(() => {
  const pm = payment_method.value
  if (!pm?.us_bank_account) return 'Bank account'
  const name = pm.us_bank_account.bank_name || 'Bank'
  const last4 = pm.us_bank_account.last4
  return last4 ? `${name} ••••${last4}` : name
})

const handleSubmit = async () => {
  if (!setup_intent.value) return

  loading.value = true
  error_message.value = ''

  try {
    const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)

    let verification_data: any = {}

    if (microdeposit_type.value === 'descriptor_code') {
      verification_data = { descriptor_code: descriptor_code.value }
    } else {
      verification_data = {
        amounts: [parseInt(amount_1.value, 10), parseInt(amount_2.value, 10)],
      }
    }

    const { error } = await stripe.verifyMicrodepositsForSetup(setup_intent.value.client_secret, verification_data)

    if (error) {
      Sentry.captureException(new Error(error.message), {
        tags: { module: 'billing', action: 'verify_microdeposits' },
        extra: {
          error_type: error.type,
          error_code: error.code,
          setup_intent_id: setup_intent.value?.stripe_setup_intent_id,
          microdeposit_type: microdeposit_type.value,
        },
      })
      console.error('[Billing] Microdeposit verification failed:', error)
      error_message.value = error.message
    } else {
      router.push(back_route.value)
    }
  } catch (err: any) {
    Sentry.captureException(err, {
      tags: { module: 'billing', action: 'verify_microdeposits' },
      extra: {
        setup_intent_id: setup_intent.value?.stripe_setup_intent_id,
        microdeposit_type: microdeposit_type.value,
      },
    })
    console.error('[Billing] Microdeposit verification exception:', err)
    error_message.value = err.message || 'Verification failed'
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  Sentry.addBreadcrumb({ category: 'billing', message: 'verify-payment-method mounted' })
  await customerStore.retrieve()
})
</script>

<template>
  <TheLayout>
    <!-- nav -->
    <VSection>
      <RouterLinkBack>
        <VButtonInvisible>← Back</VButtonInvisible>
      </RouterLinkBack>
    </VSection>
    <!-- main -->
    <VSection label="Verify Bank Account">
      <template v-if="!setup_intent">
        <p class="text-sm text-gray-600">No pending verification found for this setup intent.</p>
      </template>
      <template v-else>
        <div class="max-w-md space-y-6">
          <!-- bank info -->
          <div class="rounded border-l-4 border-[#85B5C9] bg-[#ABD0DF] p-4">
            <p class="text-sm font-medium text-gray-700">
              Microdeposits have been sent to <strong>{{ bank_label }}</strong
              >. This usually takes 1-2 business days.
            </p>
          </div>

          <!-- descriptor code form -->
          <form v-if="microdeposit_type === 'descriptor_code'" @submit.prevent="handleSubmit" class="space-y-4">
            <div>
              <label for="descriptor_code" class="block text-sm font-medium text-gray-700"> Descriptor code </label>
              <p class="mt-1 text-xs text-gray-500">
                Enter the 6-character code from your bank statement (e.g. SM11AA)
              </p>
              <input
                id="descriptor_code"
                v-model="descriptor_code"
                type="text"
                maxlength="6"
                placeholder="SM11AA"
                class="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div v-if="error_message" class="rounded border-l-4 border-red-400 bg-red-50 p-3">
              <p class="text-sm text-red-700">{{ error_message }}</p>
            </div>
            <div class="space-x-2">
              <VButton variant="primary" :click="handleSubmit" :loading="loading">Verify</VButton>
              <RouterLinkBack>
                <VButton>Go back</VButton>
              </RouterLinkBack>
            </div>
          </form>

          <!-- amounts form -->
          <form v-else @submit.prevent="handleSubmit" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700"> Microdeposit amounts </label>
              <p class="mt-1 text-xs text-gray-500">Enter the two small deposit amounts in cents (e.g. 32 and 45)</p>
              <div class="mt-2 flex space-x-3">
                <input
                  v-model="amount_1"
                  type="number"
                  min="1"
                  placeholder="32"
                  class="block w-24 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <input
                  v-model="amount_2"
                  type="number"
                  min="1"
                  placeholder="45"
                  class="block w-24 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
            <div v-if="error_message" class="rounded border-l-4 border-red-400 bg-red-50 p-3">
              <p class="text-sm text-red-700">{{ error_message }}</p>
            </div>
            <div class="space-x-2">
              <VButton variant="primary" :click="handleSubmit" :loading="loading">Verify</VButton>
              <RouterLinkBack>
                <VButton>Go back</VButton>
              </RouterLinkBack>
            </div>
          </form>
        </div>
      </template>
    </VSection>
  </TheLayout>
</template>
